/*
 * Campaigns (Marketing, Campaigns), moved over from the WordPress Steward as they were (owner, 2026-10-29: "i specially
 * want the campaigns that were for the cleaners moved over as well"). Each runs by itself once it is on: weekdays during
 * the day, a few at a time, and it stops for anyone who replies or opts out.
 *   pcal      Bring back past clients: a text a month (or season) to past one-time clients with that month's reason to
 *             book, an email 3 days later to those who did not answer (old includes/pastclients.php)
 *   rfu       Turn first-timers into regulars: texts after a one-time cleaning offering every 2 weeks or monthly at
 *             their own price (old includes/recurfu.php)
 *   offer     One-time offer: one text to clients on a date the owner picks (old includes/marketing.php, servicesdone.php)
 *   pchk      Plan check-ins: a text after a new plan's 2nd and 3rd visit (old includes/plancare.php)
 *   referral  Referrals: the referral offer automatic message, shown here too
 *
 * The words, dates and rules are the old ones. Shared here so the server, the screens and the tests use one copy.
 */
import { z } from "zod";
import { customRow } from "./custom-campaigns.js";

export const CAMPAIGN_IDS = ["pcal", "rfu", "offer", "pchk", "referral"] as const;
export type CampaignId = (typeof CAMPAIGN_IDS)[number];

export const CAMPAIGN_INFO: Record<CampaignId, { name: string; what: string }> = {
  pcal: {
    name: "Bring back past clients",
    what: "A text each month to past clients who haven't booked in a while, with that month's reason to book.",
  },
  rfu: {
    name: "Turn first-timers into regulars",
    what: "After a one-time cleaning, a few texts offering every 2 weeks or monthly at their price, with their own link to pick a day.",
  },
  offer: { name: "One-time offer", what: "One text to your clients on a date you pick, for a sale or a seasonal special." },
  pchk: {
    name: "Plan check-ins",
    what: "A text after a new plan's 2nd and 3rd visit asking how it went, so you hear about a problem before they cancel.",
  },
  referral: {
    name: "Referrals",
    what: "A text with their own link after they tap your review link. Their friend gets money off, and so do they.",
  },
};

/* ---------- Bring back past clients ---------- */

export const pcalWave = z.object({
  id: z.string().max(30),
  name: z.string().max(40),
  /** MM-DD the month's text starts. */
  start: z.string().regex(/^\d\d-\d\d$/),
  /** An email 3 days after the text. */
  mail: z.boolean(),
  subject: z.string().max(120),
  /** The words of a person's first text (and the emails). */
  first: z.string().max(400),
  /** The short words of every text after. */
  text: z.string().max(400),
});
export type PcalWave = z.infer<typeof pcalWave>;

export interface PcalConfig {
  on: boolean;
  /** When it was switched on, ms. */
  since: number;
  /** Why it stopped by itself (three failed texts), or empty. */
  paused: string;
  /** Only clients with this tag (House Cleaning on The Cleaners), or any. */
  tag: string;
  /** Only clients who had this work (The Washers: window), or any. */
  service: string;
  perDay: number;
  /** Days since their last service before they are on the list. */
  sinceDays: number;
  hello1: string;
  hello2: string;
  optout: string;
  /** Days between two texts to one person: 25 (once a month), 60 or 90. */
  gap: number;
  comeback: string;
  comebackDays: number;
  optoutReply: string;
  waves: PcalWave[];
}

const CLEANING_WAVES: PcalWave[] = [
  {
    id: "newyear",
    name: "New Year",
    start: "01-05",
    mail: true,
    subject: "A fresh start for your home?",
    first: "Start the year with the whole house spotless, top to bottom. Want us to come out in January?",
    text: "New year, fresh start. Want the whole house cleaned this month?",
  },
  {
    id: "mardigras",
    name: "Mardi Gras",
    start: "02-01",
    mail: true,
    subject: "A cleaning before Mardi Gras?",
    first: "Mardi Gras is {mardigras}. Want the whole house guest-ready before the parades, kitchen and bathrooms included?",
    text: "Mardi Gras is {mardigras}. Want the whole house guest-ready before the parades?",
  },
  {
    id: "spring",
    name: "Spring",
    start: "03-01",
    mail: true,
    subject: "Spring cleaning this year?",
    first:
      "Spring cleaning time: the whole house, including ceiling fans, blinds, baseboards and the tile and grout. Want us to do yours?",
    text: "Spring cleaning time. Want a full deep clean, fans and baseboards included?",
  },
  {
    id: "referral",
    name: "Referral",
    start: "04-05",
    mail: false,
    subject: "",
    first:
      "Our move-out cleaning covers the whole place, inside the appliances, cabinets and closets too. Know anyone moving soon?",
    text: "Know anyone moving soon who needs a move-out cleaning?",
  },
  {
    id: "mothersday",
    name: "Mother's Day",
    start: "05-01",
    mail: true,
    subject: "A cleaning for Mother's Day?",
    first:
      "Mother's Day is {mothersday}. Give mom a spotless home, or treat yourself. We bring everything. Want us to set it up?",
    text: "Mother's Day is {mothersday}. Want a cleaning for mom, or for you?",
  },
  {
    id: "summer",
    name: "Summer",
    start: "06-01",
    mail: true,
    subject: "A summer deep clean?",
    first: "We have June openings for a full summer deep clean, ceiling fans, blinds and baseboards included. Want a spot?",
    text: "We have June openings. Want a full summer deep clean?",
  },
  {
    id: "openings",
    name: "Openings",
    start: "07-06",
    mail: false,
    subject: "",
    first: "We have a few openings next week for a full home cleaning, top to bottom. Want one of them?",
    text: "We have a few openings next week. Want us to clean your home again?",
  },
  {
    id: "backtoschool",
    name: "Back to school",
    start: "08-02",
    mail: true,
    subject: "A reset cleaning for back to school?",
    first: "Kids back in school? We can reset the whole house in one visit, summer dust and all. Want us to?",
    text: "Kids back in school? Want the whole house reset this month?",
  },
  {
    id: "checkin",
    name: "Come back",
    start: "09-07",
    mail: false,
    subject: "",
    first: "Same thorough cleaning as last time, and we fix anything missed within 24 hours. Want us to come back this month?",
    text: "Want us to come back for another cleaning this month?",
  },
  {
    id: "fall",
    name: "Fall",
    start: "10-01",
    mail: true,
    subject: "A fall cleaning for your home?",
    first:
      "We're booking fall cleanings: the whole house, including ceiling fans, blinds and baseboards, before the holidays. Want a spot?",
    text: "Booking fall cleanings now. Want a full clean, fans and baseboards included?",
  },
  {
    id: "thanksgiving",
    name: "Thanksgiving",
    start: "11-02",
    mail: true,
    subject: "A cleaning before Thanksgiving?",
    first:
      "Hosting Thanksgiving? We'll get the whole house guest-ready before {thanksgiving}, kitchen and bathrooms included. Want us to come by?",
    text: "Hosting Thanksgiving? Want the whole house guest-ready by {thanksgiving}?",
  },
  {
    id: "christmas",
    name: "Christmas",
    start: "12-01",
    mail: true,
    subject: "A cleaning before Christmas?",
    first: "Holiday spots go fast. Want the whole house spotless before Christmas, kitchen and bathrooms included?",
    text: "Holiday spots go fast. Want the whole house spotless before Christmas?",
  },
];

const WASHERS_WAVES: PcalWave[] = [
  {
    id: "fall",
    name: "Fall",
    start: "10-01",
    mail: true,
    subject: "Clean windows before the holidays?",
    first: "Fall is a good time to get the windows done before the holidays, inside and out. Want us to come back out?",
    text: "Want your windows cleaned before the holidays?",
  },
  {
    id: "holidays",
    name: "Holidays",
    start: "11-20",
    mail: true,
    subject: "Clean windows for the holidays?",
    first: "Hosting for the holidays? We can have your windows spotless, inside and out, before guests arrive. Want a spot?",
    text: "Hosting for the holidays? Want clean windows before guests arrive?",
  },
  {
    id: "spring",
    name: "Spring pollen",
    start: "03-15",
    mail: true,
    subject: "Pollen on your windows?",
    first: "Pollen season leaves a yellow film on windows and screens. Want us to wash yours, inside and out?",
    text: "Pollen season is here. Want your windows and screens washed?",
  },
  {
    id: "summer",
    name: "Summer storms",
    start: "06-01",
    mail: true,
    subject: "Streaky windows after the storms?",
    first: "Summer storms leave windows streaky. Want us to come wash yours, inside and out?",
    text: "Summer storms leave windows streaky. Want them washed this month?",
  },
];

/** The trades the past client texts have words for. */
export const pcalRuns = (trade: string) => trade === "cleaning" || trade === "washers";

export function pcalDefaults(trade: string): PcalConfig {
  const wash = trade === "washers";
  return {
    on: false,
    since: 0,
    paused: "",
    tag: trade === "cleaning" ? "House Cleaning" : "",
    service: wash ? "window" : "",
    perDay: wash ? 10 : 30,
    sinceDays: wash ? 180 : 60,
    hello1: "Hi {first}, it's {business}. We {did} {when}.",
    hello2: "Hi {first}, it's {business}.",
    optout: "Reply STOP to opt out.",
    gap: 25,
    comeback: wash ? "" : "It's been a while, so if you book this month we'll clean inside your oven or fridge on us.",
    comebackDays: 365,
    optoutReply: "We unsubscribed you from our marketing messages. If you need anything, just reach out.",
    waves: (wash ? WASHERS_WAVES : CLEANING_WAVES).map((w) => ({ ...w })),
  };
}

const str = (v: unknown, d: string) => (typeof v === "string" ? v : d);
const num = (v: unknown, d: number) => (typeof v === "number" && Number.isFinite(v) ? v : d);

/**
 * A saved setting over the defaults, the way the old Steward read it: only the words of a month change, its dates and
 * name stay as written in the defaults.
 */
export function pcalMerge(trade: string, saved: unknown): PcalConfig {
  const d = pcalDefaults(trade);
  const s = saved && typeof saved === "object" ? (saved as Record<string, unknown>) : {};
  const mine = new Map<string, Record<string, unknown>>();
  for (const w of Array.isArray(s.waves) ? s.waves : [])
    if (w && typeof w === "object" && typeof (w as { id?: unknown }).id === "string")
      mine.set((w as { id: string }).id, w as Record<string, unknown>);
  const words = (v: unknown, dflt: string) => (typeof v === "string" && v.trim() !== "" ? v : dflt);
  const optoutReply = str(s.optoutReply, d.optoutReply);
  return {
    on: s.on === true,
    since: num(s.since, 0),
    paused: str(s.paused, ""),
    tag: str(s.tag, d.tag),
    service: str(s.service, d.service),
    perDay: Math.max(1, Math.min(60, num(s.perDay, d.perDay))),
    sinceDays: Math.max(30, num(s.sinceDays, d.sinceDays)),
    hello1: words(s.hello1, d.hello1),
    hello2: words(s.hello2, d.hello2),
    optout: str(s.optout, d.optout),
    gap: [25, 60, 90].includes(num(s.gap, 25)) ? num(s.gap, 25) : 25,
    comeback: str(s.comeback, d.comeback),
    comebackDays: num(s.comebackDays, d.comebackDays),
    // The answer to a stop still on its words from before 3.36.6 reads the owner's.
    optoutReply:
      optoutReply === "Got it, you won't get any more offers from us. You can still text us anytime and we'll reply."
        ? d.optoutReply
        : optoutReply,
    waves: d.waves.map((w) => {
      const m = mine.get(w.id) ?? {};
      return { ...w, text: words(m.text, w.text), first: words(m.first, w.first), subject: words(m.subject, w.subject) };
    }),
  };
}

export interface WaveOn extends PcalWave {
  /** The day this month's text started, YYYY-MM-DD. */
  date: string;
  /** The month and the year it started in (fall-2026), so each month goes to a person once. */
  key: string;
}

/** The month's text on a day: the latest start on or before it, this year or last. */
export function waveAt(day: string, waves: readonly PcalWave[]): WaveOn {
  const y = Number(day.slice(0, 4));
  let best: WaveOn | null = null;
  for (const yr of [y - 1, y])
    for (const w of waves) {
      const start = `${String(yr)}-${w.start}`;
      if (start <= day && (!best || start > best.date)) best = { ...w, date: start, key: `${w.id}-${String(yr)}` };
    }
  return best!;
}

/** The next month's text after a day. */
export function waveNext(day: string, waves: readonly PcalWave[]): WaveOn | null {
  const y = Number(day.slice(0, 4));
  let best: WaveOn | null = null;
  for (const yr of [y, y + 1])
    for (const w of waves) {
      const start = `${String(yr)}-${w.start}`;
      if (start > day && (!best || start < best.date)) best = { ...w, date: start, key: `${w.id}-${String(yr)}` };
    }
  return best;
}

const dayMs = (d: string) => Date.parse(`${d}T12:00:00Z`);

/**
 * The month's text to send on a day, or null to wait: when the next one starts within 10 days, a text now would be the
 * old words a few days before the new ones, so it waits.
 */
export function waveToSend(day: string, waves: readonly PcalWave[]): WaveOn | null {
  const n = waveNext(day, waves);
  if (n && dayMs(n.date) - dayMs(day) < 10 * 86_400_000) return null;
  return waveAt(day, waves);
}

const short = (d: Date) => d.toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric" });

/** Thanksgiving, the fourth Thursday of November: "Nov 26". */
export function thanksgiving(year: number): string {
  const first = new Date(Date.UTC(year, 10, 1));
  const toThu = (4 - first.getUTCDay() + 7) % 7;
  return short(new Date(Date.UTC(year, 10, 1 + toThu + 21)));
}

/** Easter Sunday (the Gregorian computus). */
export function easter(year: number): Date {
  const a = year % 19,
    b = Math.floor(year / 100),
    c = year % 100,
    d = Math.floor(b / 4),
    e = b % 4,
    f = Math.floor((b + 8) / 25),
    g = Math.floor((b - f + 1) / 3),
    h = (19 * a + b - d - g + 15) % 30,
    i = Math.floor(c / 4),
    k = c % 4,
    l = (32 + 2 * e + 2 * i - h - k) % 7,
    m = Math.floor((a + 11 * h + 22 * l) / 451),
    month = Math.floor((h + l - 7 * m + 114) / 31),
    day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(year, month - 1, day));
}

/** Mardi Gras, 47 days before Easter: "Feb 9". */
export const mardiGras = (year: number) => short(new Date(easter(year).getTime() - 47 * 86_400_000));

/** Mother's Day, the second Sunday of May: "May 9". */
export function mothersDay(year: number): string {
  const first = new Date(Date.UTC(year, 4, 1));
  return short(new Date(Date.UTC(year, 4, 1 + ((7 - first.getUTCDay()) % 7) + 7)));
}

/** What we did for them, from their most recent service: "did your deep cleaning", else "cleaned your home". */
export function pcalDid(trade: string, lastService: string): string {
  const n = lastService.toLowerCase();
  if (trade === "washers") {
    if (n.includes("pressure")) return "did your pressure washing";
    if (n.includes("soft") || n.includes("roof")) return "soft washed your home";
    return "cleaned your windows";
  }
  if (n.includes("deep")) return "did your deep cleaning";
  if (n.includes("move")) return "did your move cleaning";
  if (n.includes("airbnb")) return "cleaned your Airbnb";
  if (n.includes("construction")) return "did your post-construction cleaning";
  return "cleaned your home";
}

/** One person's campaign state, as the old Steward kept it on the client (pcal, rfu, pchk, offerGot). */
export interface PcalState {
  /** Month key to when its text went. */
  got?: Record<string, number>;
  /** When the last campaign text went to them (any campaign). */
  last?: number;
  /** When they were told how to opt out. Set once; no later text repeats it. */
  optAt?: number;
  /** When they last answered. */
  replied?: number;
  /** Month key to when its email went (-1: not sent, the team was texting with them). */
  mailed?: Record<string, number>;
  /** The campaign or month of their last text: a month key, "recurring" or "checkin". */
  key?: string;
}
export interface PersonState {
  pcal?: PcalState;
  rfu?: { visit?: string; sent?: number[]; stop?: string };
  pchk?: { start?: string; sent?: number[] };
  offerGot?: string;
  /** Custom campaigns by key: the day they started, when each step went (0: skipped), and why it stopped. */
  custom?: Record<string, CustomState>;
  /** Their answer to our last plan offer, as Eunice read it (receptionist/plan-answer.ts). */
  planAnswer?: PlanReply;
}
/** A no or a later holds them off plan offers after one-time cleans for 3 months (receptionist/plan-answer.ts). */
export interface PlanReply {
  said: "yes" | "no" | "later";
  at: number;
}
export interface CustomState {
  start: string;
  /** When they came in, so new work booked after it stops the campaign. */
  at: number;
  sent: number[];
  stop?: "replied" | "booked" | "stopped" | "gone";
}

export interface PcalTextFor {
  trade: string;
  business: string;
  first: string;
  /** Their last service's name, for {did}. */
  lastService: string;
  /** Their last cleaning, YYYY-MM-DD. */
  lastClean: string;
  state: PcalState;
}

/** "in March" when it was in the past 330 days, else "in 2024". */
function whenWords(lastClean: string, now: number): string {
  const t = dayMs(lastClean);
  if (t > now - 330 * 86_400_000) return `in ${new Date(t).toLocaleDateString("en-US", { timeZone: "UTC", month: "long" })}`;
  return `in ${lastClean.slice(0, 4)}`;
}

/**
 * The text a person gets for a month: the hello (the first one says who we are and when we cleaned for them), the
 * comeback offer when they have been gone a year, the month's words, and how to opt out on the first text they ever get
 * (never again after). long: the detailed words (the emails), else the detailed words only on a first text.
 */
export function pcalText(p: PcalConfig, w: WaveOn, who: PcalTextFor, now: number, long?: boolean): string {
  const first = !who.state.last;
  const year = Number(w.date.slice(0, 4));
  const fill: Record<string, string> = {
    "{first}": who.first,
    "{business}": who.business,
    "{did}": pcalDid(who.trade, who.lastService),
    "{when}": whenWords(who.lastClean, now),
    "{thanksgiving}": thanksgiving(year),
    "{mardigras}": mardiGras(year),
    "{mothersday}": mothersDay(year),
  };
  const put = (s: string) => s.replace(/\{[a-z]+\}/g, (k) => fill[k] ?? k);
  const back = p.comeback !== "" && dayMs(who.lastClean) <= now - p.comebackDays * 86_400_000 ? ` ${put(p.comeback)}` : "";
  const words = (long ?? first) && w.first.trim() !== "" ? w.first : w.text;
  let t = `${put(first ? p.hello1 : p.hello2)}${back} ${put(words)}`.trim();
  // How to opt out goes once, on the first marketing text they ever get (owner, 2026-11-02: "we only need to send it once").
  if (p.optout !== "" && !who.state.optAt) t += ` ${p.optout}`;
  return t;
}

/** A month's email: its subject and the detailed words with a hello (no STOP line), with the quote page to pick a day. */
export function pcalMail(p: PcalConfig, w: WaveOn, who: PcalTextFor, now: number, quoteUrl: string) {
  const text = pcalText(p, w, { ...who, state: { ...who.state, last: 1, optAt: now } }, now, true);
  return {
    subject: w.subject !== "" ? w.subject : who.business,
    text: `${text}\n\nTap below to pick a day, or just reply to this email.\n\nPick a day: ${quoteUrl}\n\nDon't want these emails? Reply "unsubscribe" and we'll take you off.`,
  };
}

/**
 * How a text asks to stop: "carrier" for the one-word keywords the phone company acts on (STOP, STOPALL, UNSUBSCRIBE,
 * CANCEL, END, QUIT), "words" for asking in plain words ("please stop", "no more", "not interested"), or "".
 */
export function optOutKind(body: string): "carrier" | "words" | "" {
  const t = body.trim();
  if (/^(stop|stopall|unsubscribe|cancel|end|quit)\W*$/i.test(t)) return "carrier";
  if (
    t.length <= 80 &&
    /^\W*(pl(ea)?se?\s+)?(stop|no more|remove me|take me off|unsubscribe me|opt me out|opt out|not interested|no thanks?,? not interested|don'?t text( me)?|do not text( me)?|stop (texting|sending|messaging)( me)?( these)?|leave me alone|wrong number)(\s+(pl(ea)?se?|thanks?|thank you))?\W*$/i.test(
      t,
    )
  )
    return "words";
  return "";
}

/* ---------- Turn first-timers into regulars ---------- */

export interface RfuStep {
  days: number;
  /** With their price ({weekly}, {biweekly} or {monthly}). */
  text: string;
  /** When their price can't be worked out. */
  plain: string;
}
export interface RfuConfig {
  on: boolean;
  since: number;
  /** The hello until they have had a text from us with our name. */
  hello: string;
  /** The hello once they have. */
  hello2: string;
  steps: RfuStep[];
}

export function rfuDefaults(): RfuConfig {
  return {
    on: false,
    since: 0,
    hello: "Hi {first}, it's {business}.",
    hello2: "Hi {first},",
    steps: [
      {
        days: 3,
        text: "Thanks for having us. If you'd like us back every week, it would be {weekly} a visit. Just reply and we'll set it up.",
        plain: "Thanks for having us. If you'd like us back every week, just reply and we'll set it up.",
      },
      {
        days: 14,
        text: "It's been 2 weeks since your cleaning. Want us to come back this week? It would be {biweekly}.",
        plain: "It's been 2 weeks since your cleaning. Want us to come back this week?",
      },
      {
        days: 30,
        text: "If every 2 weeks is too often, we can come once a month. That would be {monthly} a visit. Want us to come this week?",
        plain: "If every 2 weeks is too often, we can come once a month instead. Want us to come this week?",
      },
    ],
  };
}

/** The first default words: a company that saved them unchanged gets today's. */
const RFU_OLD: Record<string, string> = {
  "Thanks for having us! On an every-2-weeks plan your cleanings would be {biweekly} each, no contract. Want us to set it up?":
    "text0",
  "Thanks for having us! Want your home to stay this clean? Our every-2-weeks plan is discounted, with no contract. Want us to set it up?":
    "plain0",
  "It's been 2 weeks since your cleaning. We can come back this week for {biweekly}, what you'd pay every 2 weeks. Want us to?":
    "text1",
  "It's been 2 weeks since your cleaning, about when the dust comes back. Want us back every 2 weeks, same team when we can?":
    "plain1",
  "If every 2 weeks is a lot, we can come this week for {monthly}, what you'd pay on a monthly plan. No contract, pause anytime. Want us to?":
    "text2",
  "If every 2 weeks is a lot, monthly cleanings are discounted too. No contract, and you can pause anytime. Want us to set it up?":
    "plain2",
};

export function rfuMerge(saved: unknown): RfuConfig {
  const d = rfuDefaults();
  const s = saved && typeof saved === "object" ? (saved as Record<string, unknown>) : {};
  const steps = Array.isArray(s.steps) && s.steps.length === d.steps.length ? (s.steps as Record<string, unknown>[]) : d.steps;
  return {
    on: s.on === true,
    since: num(s.since, 0),
    hello: typeof s.hello === "string" && s.hello.trim() ? s.hello : d.hello,
    hello2: typeof s.hello2 === "string" && s.hello2.trim() ? s.hello2 : d.hello2,
    steps: steps.map((x, i) => {
      const own = (k: "text" | "plain") => {
        const v = x[k];
        return typeof v === "string" && v.trim() && RFU_OLD[v] !== `${k}${String(i)}` ? v : d.steps[i]![k];
      };
      return { days: Math.max(1, Math.min(59, num(x.days, d.steps[i]!.days))), text: own("text"), plain: own("plain") };
    }),
  };
}

/** $130, or $132.50. */
export const rfuMoney = (cents: number) =>
  `$${cents % 100 === 0 ? (cents / 100).toLocaleString("en-US") : (cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * A follow-up text: the hello, the words with their price (the plain words when it is not known), and the opt-out when
 * given (it goes on whichever text is the first to tell them). Our name is in the hello until they have had it (named).
 */
export function rfuText(
  r: RfuConfig,
  i: number,
  who: { first: string; business: string },
  price: { weekly?: number | null; biweekly: number | null; monthly: number | null },
  optout: string,
  named = false,
): string {
  const fill: Record<string, string> = { "{first}": who.first, "{business}": who.business };
  let words = r.steps[i]!.text;
  for (const f of ["weekly", "biweekly", "monthly"] as const) {
    if (!words.includes(`{${f}}`)) continue;
    const c = price[f];
    if (!c) {
      words = r.steps[i]!.plain;
      break;
    }
    fill[`{${f}}`] = rfuMoney(c);
  }
  const put = (s: string) => s.replace(/\{[a-z]+\}/g, (k) => fill[k] ?? k);
  const hello = put(named ? r.hello2 : r.hello).trim();
  // "Hi Ann," runs on into the words: "Hi Ann, it's been 2 weeks".
  if (hello.endsWith(",") && !/^I\b/.test(words)) words = words.charAt(0).toLowerCase() + words.slice(1);
  let t = `${hello} ${put(words)}`.trim();
  if (optout !== "") t += ` ${optout}`;
  return t;
}

/* ---------- Plan check-ins ---------- */

export interface PchkConfig {
  on: boolean;
  since: number;
  steps: { n: number; text: string }[];
}

export function pchkDefaults(trade: string): PchkConfig {
  const w = trade === "cleaning" ? "cleaning" : "visit";
  return {
    on: false,
    since: 0,
    steps: [
      {
        n: 2,
        text: `Hi {first}, it's {business}. How did your 2nd ${w} go? If anything was missed or you'd like something done differently, just reply and we'll fix it.`,
      },
      {
        n: 3,
        text: `Hi {first}, quick check after your 3rd ${w}. Is everything the way you like it? Tell us anything you'd change, big or small.`,
      },
    ],
  };
}

export function pchkMerge(trade: string, saved: unknown): PchkConfig {
  const d = pchkDefaults(trade);
  const s = saved && typeof saved === "object" ? (saved as Record<string, unknown>) : {};
  const steps = Array.isArray(s.steps) && s.steps.length === d.steps.length ? (s.steps as Record<string, unknown>[]) : d.steps;
  return {
    on: s.on === true,
    since: num(s.since, 0),
    steps: d.steps.map((x, i) => ({
      n: x.n,
      text: typeof steps[i]?.text === "string" && steps[i].text.trim() ? steps[i].text : x.text,
    })),
  };
}

export const pchkText = (r: PchkConfig, i: number, who: { first: string; business: string }) =>
  r.steps[i]!.text.replace(/\{first\}/g, who.first)
    .replace(/\{business\}/g, who.business)
    .trim();

/* ---------- One-time offer ---------- */

export const offerWho = z.object({
  /** Every active client, those who had a kind of work, or those who never had it. */
  mode: z.enum(["all", "had", "never"]),
  service: z.string().max(60),
  /** Had it, last done more than this many months ago (0, 3, 6, 12 or 24). */
  months: z.number().int(),
});
export type OfferWho = z.infer<typeof offerWho>;

export interface OfferConfig {
  on: boolean;
  /** A new id each time the words or date change, so a new offer can go to people who got the old one. */
  id: string;
  date: string;
  text: string;
  who: OfferWho;
}

export const offerDefaults = (): OfferConfig => ({
  on: false,
  id: "",
  date: "",
  text: "",
  who: { mode: "all", service: "", months: 0 },
});

export function cleanWho(v: unknown): OfferWho {
  const s = v && typeof v === "object" ? (v as Record<string, unknown>) : {};
  const service = typeof s.service === "string" ? s.service.trim().slice(0, 60) : "";
  let mode: OfferWho["mode"] = s.mode === "had" || s.mode === "never" ? s.mode : "all";
  if (service === "") mode = "all";
  const months = [0, 3, 6, 12, 24].includes(num(s.months, 0)) ? num(s.months, 0) : 0;
  return { mode, service: mode === "all" ? "" : service, months: mode === "had" ? months : 0 };
}

export function offerMerge(saved: unknown): OfferConfig {
  const s = saved && typeof saved === "object" ? (saved as Record<string, unknown>) : {};
  return {
    on: s.on === true,
    id: str(s.id, ""),
    date: typeof s.date === "string" && /^\d{4}-\d\d-\d\d$/.test(s.date) ? s.date : "",
    text: str(s.text, ""),
    who: cleanWho(s.who),
  };
}

/** Does a client's work history fit the offer's audience? services: name to the last day it was done (or ""). */
export function offerFits(who: OfferWho, services: ReadonlyMap<string, string>, today: string): boolean {
  if (who.mode === "all" || who.service === "") return true;
  const name = who.service.toLowerCase();
  let hit: string | undefined;
  for (const [n, last] of services) if (n.toLowerCase().trim() === name) hit = last;
  if (who.mode === "never") return hit === undefined;
  if (hit === undefined) return false;
  if (who.months <= 0 || !/^\d{4}-\d\d-\d\d$/.test(hit)) return true;
  const [y, m, d] = today.split("-").map(Number) as [number, number, number];
  const cut = new Date(Date.UTC(y, m - 1 - who.months, d)).toISOString().slice(0, 10);
  return hit <= cut;
}

/* ---------- The counts (old steward_pcal_log) ---------- */

export interface CampaignLog {
  day: string;
  sent: number;
  fails: number;
  /** The local hour the campaigns last ran, so they work once an hour. */
  hour?: string;
  /** Per month key, "recurring" or "checkin": sent, mailed, replied, stopped, and plan offer answers (said no, said later). */
  waves: Record<string, Partial<Record<WaveCount, number>>>;
}

export type WaveCount = "sent" | "mailed" | "replied" | "stopped" | "booked" | "saidNo" | "saidLater";

export function logMerge(saved: unknown): CampaignLog {
  const s = saved && typeof saved === "object" ? (saved as Record<string, unknown>) : {};
  return {
    day: str(s.day, ""),
    sent: num(s.sent, 0),
    fails: num(s.fails, 0),
    hour: str(s.hour, ""),
    waves: s.waves && typeof s.waves === "object" && !Array.isArray(s.waves) ? (s.waves as CampaignLog["waves"]) : {},
  };
}

/* ---------- API shapes ---------- */

export const campaignRow = z.object({
  id: z.enum(CAMPAIGN_IDS),
  name: z.string(),
  what: z.string(),
  on: z.boolean(),
  paused: z.boolean(),
  line: z.string(),
});
export type CampaignRow = z.infer<typeof campaignRow>;

const tiles = z.record(z.string(), z.number());

export const pcalPage = z.object({
  on: z.boolean(),
  paused: z.string(),
  cap: z.number(),
  service: z.string(),
  sinceDays: z.number(),
  sentToday: z.number(),
  hello1: z.string(),
  hello2: z.string(),
  optout: z.string(),
  optoutReply: z.string(),
  comeback: z.string(),
  comebackDays: z.number(),
  tag: z.string(),
  gap: z.number(),
  waves: z.array(pcalWave),
  now: z.object({ key: z.string(), id: z.string(), name: z.string(), date: z.string() }),
  next: z.object({ name: z.string(), date: z.string() }).nullable(),
  upNext: z.array(z.object({ id: z.string(), name: z.string(), last: z.string(), jobs: z.number(), texted: z.boolean() })),
  bookedWho: z.array(z.string()),
  people: z.number(),
  withEmail: z.number(),
  left: z.number(),
  stats: tiles,
  sample: z.object({ name: z.string(), first: z.string(), later: z.string() }).nullable(),
});
export type PcalPage = z.infer<typeof pcalPage>;

export const pcalPreview = z.object({
  season: z.string(),
  gap: z.number(),
  for: z.string(),
  people: z.array(
    z.object({ name: z.string(), phone: z.string(), last: z.string(), email: z.boolean(), got: z.boolean(), text: z.string() }),
  ),
  year: z.array(
    z.object({
      name: z.string(),
      date: z.string(),
      text: z.string(),
      email: z.boolean(),
      mail: z.object({ date: z.string(), subject: z.string(), text: z.string() }).nullable(),
    }),
  ),
});
export type PcalPreview = z.infer<typeof pcalPreview>;

export const rfuPage = z.object({
  on: z.boolean(),
  hello: z.string(),
  hello2: z.string(),
  steps: z.array(z.object({ days: z.number(), text: z.string(), plain: z.string() })),
  now: z.number(),
  followed: z.number(),
  converted: z.number(),
  stats: tiles,
  samples: z.array(z.string()),
  plainSamples: z.array(z.string()),
});
export type RfuPage = z.infer<typeof rfuPage>;

export const pchkPage = z.object({
  on: z.boolean(),
  steps: z.array(z.object({ n: z.number(), text: z.string() })),
  samples: z.array(z.string()),
  stats: tiles,
});
export type PchkPage = z.infer<typeof pchkPage>;

export const offerPage = z.object({
  on: z.boolean(),
  date: z.string(),
  text: z.string(),
  who: offerWho,
  /** How many active clients with a mobile number the audience picks now. */
  count: z.number(),
  sent: z.number(),
  /** Kinds of work clients have had, for the picker. */
  services: z.array(z.string()),
});
export type OfferPage = z.infer<typeof offerPage>;

export const campaignsAnswer = z.object({
  rows: z.array(campaignRow),
  /** The team's own campaigns (custom-campaigns.ts). */
  custom: z.array(customRow),
  names: z.record(z.string(), z.string()),
});
export type CampaignsAnswer = z.infer<typeof campaignsAnswer>;

const text = (max: number) => z.string().max(max);

export const pcalPatch = z
  .object({
    on: z.boolean(),
    resume: z.boolean(),
    hello1: text(200),
    hello2: text(200),
    optout: text(80),
    optoutReply: text(300),
    comeback: text(200),
    gap: z.union([z.literal(25), z.literal(60), z.literal(90)]),
    waves: z.array(z.object({ id: z.string().max(30), text: text(400), first: text(400), subject: text(120) })).max(20),
    /** Don't text on Up next: that client gets no marketing texts from now on. */
    skip: z.uuid(),
  })
  .partial()
  .strict();
export type PcalPatch = z.infer<typeof pcalPatch>;

export const rfuPatch = z
  .object({
    on: z.boolean(),
    hello: text(120),
    hello2: text(120),
    steps: z.array(z.object({ days: z.number().int().min(1).max(59), text: text(300), plain: text(300) })).max(3),
  })
  .partial()
  .strict();
export type RfuPatch = z.infer<typeof rfuPatch>;

export const pchkPatch = z
  .object({ on: z.boolean(), steps: z.array(z.object({ text: text(300) })).max(2) })
  .partial()
  .strict();
export type PchkPatch = z.infer<typeof pchkPatch>;

export const offerPatch = z
  .object({
    on: z.boolean(),
    date: z.string().regex(/^(\d{4}-\d\d-\d\d)?$/),
    text: text(600),
    who: offerWho,
  })
  .partial()
  .strict();
export type OfferPatch = z.infer<typeof offerPatch>;

export const campaignName = z.object({ name: text(60) }).strict();
